import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EventNote as EventNoteIcon,
  History as HistoryIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  NoFood as WithdrawalIcon,
  Paid as PaidIcon,
  PieChart as ComplianceIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  useBatchSchedule,
  useBatchUpcoming,
  useRegenerateBatch,
  useAssignBatchProgramme,
  useAssignTask,
  usePreventiveProgrammes,
  useTreatmentHistory,
  useWithdrawalStatus,
  useCompliance,
} from '../hooks/usePreventiveHealth';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  useBirdProcurement,
  useBirdBatch,
  useBirdProcurements,
  useUpdateBird,
  useDeleteBird,
  useUpdateBirdProcurement,
  useCreateBird,
  useMarkBirdProcurementAsDelivered,
} from '../hooks/useBirds';
import { useUsers } from '../hooks/useBirdProcurementAssignees';
import { Bird, BirdProcurement } from '../types';

// ─── helpers ───────────────────────────────────────────────────────────────

const parseMoney = (s: string | null | undefined): number =>
  parseFloat((s ?? '').replace(/GH₵\s*/, '').replace(/,/g, '')) || 0;

const fmtMoney = (n: number): string =>
  `GH₵ ${n.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (d?: string | null): string => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`;
};

function uniqueSupplierNames(
  birds: Array<{ supplier?: { name: string } | null } | null | undefined>
): string {
  const seen = new Set<string>();
  const names: string[] = [];
  (birds ?? []).forEach((b) => {
    const n = b?.supplier?.name;
    if (n && !seen.has(n)) {
      seen.add(n);
      names.push(n);
    }
  });
  return names.join(', ');
}

// ─── shared design tokens ───────────────────────────────────────────────────

const TRACK_COLORS: Record<string, string> = {
  vaccination: '#42a5f5',
  antiparasitic: '#ff9800',
  vitamin: '#66bb6a',
  biosecurity: '#ab47bc',
  monitoring: '#ef5350',
};

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  scheduled: 'info',
  in_progress: 'warning',
  completed: 'success',
  skipped: 'default',
  missed: 'error',
};

const dataGridSx = {
  border: 0,
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.05)',
    borderBottom: '1px solid rgba(255,152,0,0.12)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.6)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    {
      color: 'rgba(255,255,255,0.5)',
      fontSize: '0.78rem',
    },
  '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
};

// ─── sub-components ────────────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'rgba(255,255,255,0.3)',
      letterSpacing: '0.8px',
      fontSize: '0.62rem',
      textTransform: 'uppercase',
      display: 'block',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    py={0.75}
    sx={{ borderBottom: '1px solid rgba(255,255,255,0.04)', '&:last-child': { border: 0 } }}
  >
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(255,255,255,0.4)',
        fontWeight: 600,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        fontSize: '0.62rem',
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff', textAlign: 'right' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
}> = ({ icon, label, value, sub, accent }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 175,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${accent}22`,
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        right: 14,
        top: '50%',
        transform: 'translateY(-50%)',
        opacity: 0.06,
        fontSize: 56,
        color: accent,
        display: 'flex',
      }}
    >
      {icon}
    </Box>
    <SectionLabel>{label}</SectionLabel>
    <Typography variant="h5" fontWeight={800} sx={{ color: accent, lineHeight: 1, mb: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.68rem' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── BatchScheduleContent ──────────────────────────────────────────────────

const BatchScheduleContent: React.FC<{ batchId: number }> = ({ batchId }) => {
  const [subTab, setSubTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedProgrammeId, setSelectedProgrammeId] = useState<string>('');
  const [taskAssignRow, setTaskAssignRow] = useState<any | null>(null);
  const [taskAssignUserId, setTaskAssignUserId] = useState<string>('');

  const { data: batchData } = useBirdBatch(batchId);
  const { data, isLoading } = useBatchSchedule(batchId);
  const { data: upcomingData, isLoading: upcomingLoading } = useBatchUpcoming(batchId, {
    days: 30,
  });
  const { data: programmesData } = usePreventiveProgrammes();
  const { data: usersData } = useUsers();
  const regenerate = useRegenerateBatch();
  const assignProgramme = useAssignBatchProgramme();
  const assignTask = useAssignTask();

  const users: any[] = usersData ?? [];
  const currentProgramme = batchData?.programme ?? null;
  const programmes: any[] = React.useMemo(
    () => (Array.isArray(programmesData) ? programmesData : (programmesData?.data ?? [])),
    [programmesData]
  );

  const handleOpenTaskAssign = (row: any) => {
    setTaskAssignRow(row);
    setTaskAssignUserId(row.assigned_to?.id?.toString() ?? row.assignedTo?.id?.toString() ?? '');
  };
  const handleTaskAssign = () => {
    if (!taskAssignRow) return;
    assignTask.mutate(
      { eventId: taskAssignRow.id, userId: taskAssignUserId ? Number(taskAssignUserId) : null },
      { onSettled: () => setTaskAssignRow(null) }
    );
  };

  const rows = React.useMemo(() => {
    const events: any[] = Array.isArray(data) ? data : (data?.data ?? []);
    return events
      .filter(
        (e) =>
          (!statusFilter || e.status === statusFilter) &&
          (!trackFilter || e.event?.track === trackFilter)
      )
      .map((e, idx) => ({ ...e, id: e.id ?? idx }));
  }, [data, statusFilter, trackFilter]);

  const upcomingRows: any[] = React.useMemo(() => {
    const items: any[] = Array.isArray(upcomingData) ? upcomingData : (upcomingData?.data ?? []);
    return items.map((e, idx) => ({ ...e, id: e.id ?? idx }));
  }, [upcomingData]);

  const columns: GridColDef[] = [
    { field: 'scheduled_date', headerName: 'Date', width: 120 },
    {
      field: 'event_name',
      headerName: 'Event',
      flex: 1,
      valueGetter: (_v: any, row: any) => row.event?.name,
    },
    {
      field: 'track',
      headerName: 'Track',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={p.row.event?.track ?? '—'}
          size="small"
          sx={{
            bgcolor: TRACK_COLORS[p.row.event?.track] ?? 'rgba(255,255,255,0.1)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.65rem',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={p.value}
          size="small"
          color={STATUS_COLORS[p.value] ?? 'default'}
          sx={{ fontSize: '0.65rem', fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'product',
      headerName: 'Product',
      width: 160,
      valueGetter: (_v: any, row: any) => row.event?.product?.name ?? '—',
    },
    {
      field: 'assigned_to',
      headerName: 'Assigned To',
      width: 140,
      valueGetter: (_v: any, row: any) => row.assignedTo?.name ?? '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Button
          size="small"
          variant="text"
          onClick={() => handleOpenTaskAssign(p.row)}
          sx={{ textTransform: 'none', color: '#ff9800', fontSize: '0.75rem' }}
        >
          Assign
        </Button>
      ),
    },
  ];

  const upcomingColumns: GridColDef[] = columns.filter((c) => c.field !== 'status');

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} sx={{ color: '#ff9800' }} />
      </Box>
    );

  return (
    <Box>
      {/* Programme bar */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        mb={2.5}
        px={2}
        py={1.5}
        sx={{
          bgcolor: 'rgba(255,152,0,0.05)',
          border: '1px solid rgba(255,152,0,0.1)',
          borderRadius: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          Programme
        </Typography>
        {currentProgramme ? (
          <Chip
            label={currentProgramme.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', fontSize: '0.75rem' }}
          >
            None assigned
          </Typography>
        )}
        <Button
          size="small"
          onClick={() => {
            setSelectedProgrammeId(currentProgramme?.id?.toString() ?? '');
            setAssignOpen(true);
          }}
          sx={{
            ml: 'auto',
            textTransform: 'none',
            color: '#ff9800',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {currentProgramme ? 'Change' : 'Assign'}
        </Button>
        <Tooltip title="Regenerate schedule from programme">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setConfirmRegen(true)}
              disabled={regenerate.isPending}
              sx={{
                textTransform: 'none',
                color: 'rgba(255,255,255,0.5)',
                borderColor: 'rgba(255,255,255,0.12)',
                borderRadius: 1.5,
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.3)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Regenerate
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Sub-tabs */}
      <Tabs
        value={subTab}
        onChange={(_e, v) => setSubTab(v)}
        sx={{
          mb: 2,
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0,
            textTransform: 'none',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.45)',
            fontSize: '0.8rem',
          },
          '& .Mui-selected': { color: '#ff9800' },
          '& .MuiTabs-indicator': { bgcolor: '#ff9800', height: 2 },
        }}
      >
        <Tab label="Full Schedule" />
        <Tab
          label={
            upcomingRows.length > 0 ? `Upcoming (${upcomingRows.length})` : 'Upcoming (30 days)'
          }
          icon={<CalendarIcon sx={{ fontSize: 13 }} />}
          iconPosition="start"
        />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Box display="flex" gap={1.5} mb={2}>
            {[
              {
                label: 'Status',
                value: statusFilter,
                setter: setStatusFilter,
                opts: ['scheduled', 'in_progress', 'completed', 'skipped', 'missed'],
              },
              {
                label: 'Track',
                value: trackFilter,
                setter: setTrackFilter,
                opts: Object.keys(TRACK_COLORS),
              },
            ].map(({ label, value, setter, opts }) => (
              <FormControl key={label} size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>{label}</InputLabel>
                <Select
                  value={value}
                  label={label}
                  onChange={(e) => setter(e.target.value as string)}
                  sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {opts.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            rowHeight={48}
            sx={dataGridSx}
          />
        </Box>
      )}

      {subTab === 1 &&
        (upcomingLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} sx={{ color: '#ff9800' }} />
          </Box>
        ) : upcomingRows.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
              No events scheduled in the next 30 days.
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={upcomingRows}
            columns={upcomingColumns}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            rowHeight={48}
            sx={dataGridSx}
          />
        ))}

      {/* Task assign dialog */}
      <Dialog
        open={!!taskAssignRow}
        onClose={() => setTaskAssignRow(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Assign Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
            {taskAssignRow?.event?.name ?? 'Task'} — {taskAssignRow?.scheduled_date ?? ''}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Assign to</InputLabel>
            <Select
              value={taskAssignUserId}
              label="Assign to"
              onChange={(e) => setTaskAssignUserId(e.target.value as string)}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id.toString()}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTaskAssignRow(null)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={assignTask.isPending}
            onClick={handleTaskAssign}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {assignTask.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign programme dialog */}
      <Dialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Assign Programme</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Programme</InputLabel>
            <Select
              value={selectedProgrammeId}
              label="Programme"
              onChange={(e) => setSelectedProgrammeId(e.target.value as string)}
              sx={{ borderRadius: 1.5 }}
            >
              <MenuItem value="">
                <em>None (remove)</em>
              </MenuItem>
              {programmes.map((p) => (
                <MenuItem key={p.id} value={p.id.toString()}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedProgrammeId && selectedProgrammeId !== currentProgramme?.id?.toString() && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={false}>
              Assigning a new programme will generate scheduled events from today.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAssignOpen(false)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              assignProgramme.isPending ||
              !selectedProgrammeId ||
              selectedProgrammeId === currentProgramme?.id?.toString()
            }
            onClick={() =>
              assignProgramme.mutate(
                { batchId, programmeId: selectedProgrammeId ? Number(selectedProgrammeId) : null },
                { onSettled: () => setAssignOpen(false) }
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {assignProgramme.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regen confirm */}
      <Dialog
        open={confirmRegen}
        onClose={() => setConfirmRegen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Regenerate Schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            This will rebuild scheduled events from the assigned programme, adding missing future
            events. Completed events are not affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmRegen(false)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={regenerate.isPending}
            onClick={() => regenerate.mutate(batchId, { onSettled: () => setConfirmRegen(false) })}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── TreatmentHistoryContent ────────────────────────────────────────────────

const TreatmentHistoryContent: React.FC<{ batchId: number }> = ({ batchId }) => {
  const { data, isLoading } = useTreatmentHistory({ batch_id: batchId, per_page: 100 });

  const rows = React.useMemo(() => {
    const items: any[] = Array.isArray(data) ? data : (data?.data ?? []);
    return items.map((r, idx) => ({ ...r, id: r.id ?? idx }));
  }, [data]);

  const columns: GridColDef[] = [
    { field: 'completed_at', headerName: 'Date', width: 160 },
    {
      field: 'event',
      headerName: 'Event',
      flex: 1,
      valueGetter: (_v: any, row: any) => row.event?.name ?? row.event,
    },
    {
      field: 'track',
      headerName: 'Track',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={p.value ?? '—'}
          size="small"
          sx={{
            bgcolor: TRACK_COLORS[p.value] ?? 'rgba(255,255,255,0.1)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.65rem',
          }}
        />
      ),
    },
    { field: 'product_used', headerName: 'Product', width: 160 },
    { field: 'actual_dose', headerName: 'Dose', width: 100 },
    { field: 'birds_treated', headerName: 'Birds', width: 80 },
    { field: 'completed_by', headerName: 'Completed By', width: 140 },
  ];

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} sx={{ color: '#ff9800' }} />
      </Box>
    );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
      pageSizeOptions={[10, 25, 50]}
      disableRowSelectionOnClick
      autoHeight
      rowHeight={48}
      sx={dataGridSx}
    />
  );
};

// ─── WithdrawalStatusContent ────────────────────────────────────────────────

const WithdrawalStatusContent: React.FC<{ batchId: number }> = ({ batchId }) => {
  const { data, isLoading } = useWithdrawalStatus({ batch_id: batchId });
  const items: any[] = React.useMemo(
    () => (Array.isArray(data) ? data : (data?.data ?? [])),
    [data]
  );

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} sx={{ color: '#ff9800' }} />
      </Box>
    );

  if (!items.length)
    return (
      <Box
        sx={{
          py: 4,
          textAlign: 'center',
          border: '1px solid rgba(76,175,80,0.2)',
          borderRadius: 2,
          background: 'rgba(76,175,80,0.05)',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
        <Typography variant="body1" fontWeight={600} sx={{ color: '#4caf50' }}>
          No active withdrawal periods
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)', mt: 0.5 }}>
          Eggs from this batch are safe to sell.
        </Typography>
      </Box>
    );

  const hasActive = items.some((i) => i.days_remaining > 0);

  return (
    <Box>
      {hasActive && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          Eggs cannot be sold during an active withdrawal period.
        </Alert>
      )}
      <Box display="flex" flexDirection="column" gap={1.5}>
        {items.map((item, idx) => {
          const days = item.days_remaining ?? 0;
          const chipColor =
            days <= 0 ? '#4caf50' : days <= 3 ? '#f44336' : days <= 7 ? '#ff9800' : '#42a5f5';
          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 2.5,
                border: `1px solid ${chipColor}22`,
                borderLeft: `3px solid ${chipColor}`,
                borderRadius: 2,
                background: `${chipColor}06`,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography fontWeight={700} sx={{ color: '#ffffff', mb: 0.5 }}>
                    {item.event_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                    Product: {item.product}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                    Until: {item.egg_withdrawal_until}
                  </Typography>
                </Box>
                <Chip
                  label={days <= 0 ? 'Cleared' : `${days}d remaining`}
                  size="small"
                  sx={{
                    bgcolor: `${chipColor}20`,
                    color: chipColor,
                    border: `1px solid ${chipColor}40`,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── ComplianceContent ──────────────────────────────────────────────────────

const ComplianceContent: React.FC<{ batchId: number }> = ({ batchId }) => {
  const { data, isLoading } = useCompliance({ batch_id: batchId });
  const stats = React.useMemo(() => {
    const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
    return rows[0] ?? null;
  }, [data]);

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} sx={{ color: '#ff9800' }} />
      </Box>
    );

  if (!stats)
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No compliance data available.
      </Alert>
    );

  const rate = parseFloat(stats.compliance_rate ?? 0);
  const rateColor = rate >= 80 ? '#66bb6a' : rate >= 60 ? '#ffa726' : '#ef5350';

  return (
    <Box>
      {/* Rate display */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${rateColor}22`,
          borderLeft: `3px solid ${rateColor}`,
          borderRadius: 2.5,
          background: `${rateColor}06`,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box textAlign="center" flexShrink={0}>
          <Typography
            variant="h2"
            fontWeight={900}
            sx={{ color: rateColor, lineHeight: 1, letterSpacing: '-0.03em' }}
          >
            {rate.toFixed(1)}%
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.68rem',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
            }}
          >
            Compliance
          </Typography>
        </Box>
        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" mb={0.75}>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}
            >
              Programme adherence
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: rateColor, fontSize: '0.7rem' }}
            >
              {rate >= 80 ? 'Excellent' : rate >= 60 ? 'Moderate' : 'Needs attention'}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(rate, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: rateColor, borderRadius: 4 },
            }}
          />
        </Box>
      </Paper>

      {/* Stats grid */}
      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2}>
        {[
          { label: 'Total Events', value: stats.total, color: '#ff9800' },
          { label: 'Completed', value: stats.completed, color: '#66bb6a' },
          { label: 'Missed', value: stats.missed, color: '#ef5350' },
          { label: 'Overdue', value: stats.overdue, color: '#ffa726' },
        ].map((s) => (
          <Paper
            key={s.label}
            elevation={0}
            sx={{
              p: 2.5,
              textAlign: 'center',
              background: '#111318',
              border: '1px solid rgba(255,255,255,0.05)',
              borderTop: `3px solid ${s.color}`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ color: s.color, lineHeight: 1, mb: 0.5 }}
            >
              {s.value ?? 0}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem', letterSpacing: '0.4px' }}
            >
              {s.label}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const ProcurementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [editingBird, setEditingBird] = useState<Bird | null>(null);
  const [deletingBird, setDeletingBird] = useState<Bird | null>(null);
  const [editForm, setEditForm] = useState({
    identifier: '',
    color: '',
    quantity_bought: '',
    unit_price: '',
    laying_start_date: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const procurementId = parseInt(id || '0');
  const {
    data: procurement,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useBirdProcurement(procurementId, !!procurementId);
  const { data: allProcurementsData } = useBirdProcurements({ per_page: 100 });
  const allProcurements: BirdProcurement[] = allProcurementsData?.data ?? [];

  const [editingProcQty, setEditingProcQty] = useState(false);
  const [procQtyForm, setProcQtyForm] = useState('');
  const [deliveringProcurement, setDeliveringProcurement] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryQtys, setDeliveryQtys] = useState<Record<number, string>>({});
  const [addingBird, setAddingBird] = useState(false);
  const [addBirdForm, setAddBirdForm] = useState({
    identifier: '',
    color: 'White',
    quantity_bought: '',
    unit_price: '',
  });

  const updateBirdMutation = useUpdateBird();
  const deleteBirdMutation = useDeleteBird();
  const updateProcurementMutation = useUpdateBirdProcurement();
  const createBirdMutation = useCreateBird();
  const markDeliveredMutation = useMarkBirdProcurementAsDelivered();

  const handleOpenDelivery = () => {
    const today = new Date().toISOString().split('T')[0];
    setDeliveryDate(today);
    const qtys: Record<number, string> = {};
    (procurement?.birds ?? []).forEach((b: any) => {
      qtys[b.id] = String(b.quantity_bought ?? '');
    });
    setDeliveryQtys(qtys);
    setDeliveringProcurement(true);
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryDate) return;
    const birds = (procurement?.birds ?? []).map((b: any) => ({
      bird_id: b.id,
      quantity_delivered: parseInt(deliveryQtys[b.id] ?? '0', 10),
      date_delivered: deliveryDate,
    }));
    try {
      await markDeliveredMutation.mutateAsync({ id: procurementId, birds });
      setSnackbar({ open: true, message: 'Procurement marked as delivered', severity: 'success' });
      setDeliveringProcurement(false);
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Failed to mark as delivered',
        severity: 'error',
      });
    }
  };

  const handleAddBird = async () => {
    const qty = parseInt(addBirdForm.quantity_bought, 10);
    if (!addBirdForm.identifier || isNaN(qty) || qty < 1) return;
    try {
      const unitPrice = parseFloat(addBirdForm.unit_price);
      await createBirdMutation.mutateAsync({
        identifier: addBirdForm.identifier,
        color: addBirdForm.color,
        quantity_bought: qty,
        procurement_id: procurementId,
        ...(isNaN(unitPrice)
          ? {}
          : {
              unit_price: unitPrice,
              total_cost: unitPrice * qty,
            }),
      } as any);
      setSnackbar({ open: true, message: 'Bird group added', severity: 'success' });
      setAddingBird(false);
      setAddBirdForm({ identifier: '', color: 'White', quantity_bought: '', unit_price: '' });
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Failed to add bird group',
        severity: 'error',
      });
    }
  };

  const handleOpenProcQtyEdit = () => {
    setProcQtyForm(
      String(
        (procurement?.birds ?? []).reduce((s: number, b: any) => s + (b.quantity_bought ?? 0), 0) ||
          procurement?.total_quantity ||
          ''
      )
    );
    setEditingProcQty(true);
  };

  const handleSaveProcQty = async () => {
    const qty = parseInt(procQtyForm, 10);
    if (isNaN(qty) || qty < 1) return;
    try {
      await updateProcurementMutation.mutateAsync({
        id: procurementId,
        data: { total_quantity: qty } as any,
      });
      setSnackbar({ open: true, message: 'Quantity updated', severity: 'success' });
      setEditingProcQty(false);
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Update failed',
        severity: 'error',
      });
    }
  };

  const handleOpenEdit = (bird: Bird) => {
    setEditingBird(bird);
    setEditForm({
      identifier: bird.identifier,
      color: bird.color,
      quantity_bought: bird.quantity_bought.toString(),
      unit_price: bird.unit_price ? String(parseMoney(bird.unit_price)) : '',
      laying_start_date: bird.laying_start_date ?? '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingBird) return;
    try {
      const data: Record<string, any> = {
        identifier: editForm.identifier,
        color: editForm.color,
      };
      const qty = parseInt(editForm.quantity_bought, 10);
      if (!isNaN(qty) && qty > 0) data.quantity_bought = qty;
      const unitPrice = parseFloat(editForm.unit_price);
      if (!isNaN(unitPrice)) {
        data.unit_price = unitPrice;
        data.total_cost = unitPrice * (isNaN(qty) ? editingBird.quantity_bought : qty);
      }
      if (editForm.laying_start_date) data.laying_start_date = editForm.laying_start_date;
      await updateBirdMutation.mutateAsync({ id: editingBird.id, data });
      setSnackbar({ open: true, message: 'Bird group updated', severity: 'success' });
      setEditingBird(null);
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Update failed',
        severity: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingBird) return;
    try {
      await deleteBirdMutation.mutateAsync(deletingBird.id);
      setSnackbar({ open: true, message: 'Bird group deleted', severity: 'success' });
      setDeletingBird(null);
      refetch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Delete failed',
        severity: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#ff9800' }} />
      </Box>
    );
  }

  if (error || !procurement) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff', mb: 1.5 }}>
            Procurement not found
          </Typography>
          <Button
            onClick={() => navigate('/procurements')}
            sx={{ textTransform: 'none', color: '#ff9800', fontWeight: 600 }}
          >
            ← Back to Procurements
          </Button>
        </Box>
      </Box>
    );
  }

  const batch = (procurement as any).batch ?? null;
  const batchId: number | null = batch?.id ?? null;
  const isDelivered = !!procurement.date_delivered;
  const status = (procurement as any).status;
  const totalDelivered = procurement.total_quantity ?? 0;
  const totalBirdsProcured = !isDelivered
    ? (procurement.birds ?? []).reduce((sum: number, b: any) => sum + (b.quantity_bought ?? 0), 0)
    : totalDelivered;
  const totalInvestment = fmtMoney(
    (procurement.birds ?? []).reduce((sum: number, b: any) => sum + parseMoney(b.total_cost), 0)
  );

  const statusColor =
    status === 'delivered' ? '#4caf50' : status === 'partially_delivered' ? '#42a5f5' : '#9e9e9e';
  const statusLabel =
    status === 'delivered'
      ? 'Delivered'
      : status === 'partially_delivered'
        ? 'Partially Delivered'
        : 'Pending Delivery';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffcc02 50%, #ff9800 100%)',
            },
          }}
        />
      )}

      {/* ── Page Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.06) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ p: { xs: 2.5, md: 3 }, position: 'relative' }}>
          {/* Top row: back + switcher */}
          <Box display="flex" alignItems="center" gap={2} mb={2.5} flexWrap="wrap">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/procurements')}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.25)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Procurements
            </Button>
            <Box sx={{ flex: 1 }} />
            {!isDelivered && (procurement?.birds ?? []).length > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<LocalShippingIcon />}
                onClick={handleOpenDelivery}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' },
                }}
              >
                Mark as Delivered
              </Button>
            )}
            <Autocomplete
              options={allProcurements}
              getOptionLabel={(p: BirdProcurement) => p.identifier}
              value={allProcurements.find((p) => p.id === procurementId) ?? null}
              onChange={(_e, selected: BirdProcurement | null) => {
                if (selected) navigate(`/procurements/${selected.id}`);
              }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              sx={{ minWidth: 220 }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Switch batch…"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.identifier}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                      {uniqueSupplierNames(option.birds ?? []) || '—'} ·{' '}
                      {option.total_quantity?.toLocaleString()} birds
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>

          {/* Main identity row */}
          <Box display="flex" alignItems="center" gap={2.5} flexWrap="wrap">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                flexShrink: 0,
                background:
                  'linear-gradient(135deg, rgba(255,152,0,0.18) 0%, rgba(255,152,0,0.06) 100%)',
                border: '1px solid rgba(255,152,0,0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography
                  variant="h4"
                  fontWeight={900}
                  sx={{
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    fontSize: { xs: '1.4rem', md: '1.8rem' },
                  }}
                >
                  {procurement.identifier}
                </Typography>
                <Chip
                  label={statusLabel}
                  size="small"
                  icon={
                    status === 'delivered' ? (
                      <CheckCircleIcon
                        sx={{ fontSize: '0.75rem !important', color: `${statusColor} !important` }}
                      />
                    ) : status === 'partially_delivered' ? (
                      <LocalShippingIcon
                        sx={{ fontSize: '0.75rem !important', color: `${statusColor} !important` }}
                      />
                    ) : (
                      <HourglassIcon
                        sx={{ fontSize: '0.75rem !important', color: `${statusColor} !important` }}
                      />
                    )
                  }
                  sx={{
                    height: 24,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: `${statusColor}15`,
                    color: statusColor,
                    border: `1px solid ${statusColor}35`,
                    '& .MuiChip-icon': { ml: '4px' },
                  }}
                />
                {batch?.programme && (
                  <Chip
                    label={batch.programme.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} mt={0.5} flexWrap="wrap">
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}
                >
                  #{procurement.id}
                </Typography>
                {(procurement.birds ?? []).length > 0 && (
                  <>
                    <Box
                      sx={{
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.15)',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}
                    >
                      {uniqueSupplierNames(procurement.birds ?? [])}
                    </Typography>
                  </>
                )}
                <Box
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.15)',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}
                >
                  Procured {formatDate(procurement.date_procured)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Stat Cards ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        <Box sx={{ flex: 1, minWidth: 175, position: 'relative' }}>
          <StatCard
            icon={<InventoryIcon sx={{ fontSize: 56 }} />}
            label="Total Birds Procured"
            value={totalBirdsProcured > 0 ? totalBirdsProcured.toLocaleString() : '—'}
            sub={`${(procurement.birds ?? []).length} strain${(procurement.birds ?? []).length !== 1 ? 's' : ''}`}
            accent="#ff9800"
          />
          {!isDelivered && (
            <IconButton
              size="small"
              onClick={handleOpenProcQtyEdit}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 22,
                height: 22,
                bgcolor: 'rgba(255,152,0,0.12)',
                '&:hover': { bgcolor: 'rgba(255,152,0,0.22)' },
              }}
            >
              <EditIcon sx={{ fontSize: 11, color: '#ff9800' }} />
            </IconButton>
          )}
        </Box>
        <StatCard
          icon={<LocalShippingIcon sx={{ fontSize: 56 }} />}
          label="Birds Delivered"
          value={isDelivered ? totalDelivered.toLocaleString() : '—'}
          sub={isDelivered ? formatDate(procurement.date_delivered) : 'not yet delivered'}
          accent="#66bb6a"
        />
        <StatCard
          icon={<PaidIcon sx={{ fontSize: 56 }} />}
          label="Total Investment"
          value={totalInvestment}
          sub={`procured ${formatDate(procurement.date_procured)}`}
          accent="#42a5f5"
        />
        <StatCard
          icon={<ShoppingCartIcon sx={{ fontSize: 56 }} />}
          label="Suppliers"
          value={(() => {
            const seen = new Set<number>();
            return (
              (procurement.birds ?? []).filter(
                (b) => b.supplier && !seen.has(b.supplier!.id) && !!seen.add(b.supplier!.id)
              ).length || '—'
            );
          })()}
          sub={uniqueSupplierNames(procurement.birds ?? []) || '—'}
          accent="#ba68c8"
        />
      </Box>

      {/* ── Tabs Panel ── */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            px: 2,
            background: 'linear-gradient(90deg, rgba(255,152,0,0.05) 0%, transparent 100%)',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                fontSize: '0.82rem',
                gap: 0.5,
              },
              '& .Mui-selected': { color: '#ff9800' },
              '& .MuiTabs-indicator': { backgroundColor: '#ff9800', height: 2 },
              '& .MuiTab-iconWrapper': { mr: 0 },
            }}
          >
            <Tab icon={<InfoIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Details" />
            <Tab
              icon={<EventNoteIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="Schedule"
              disabled={!batchId}
            />
            <Tab
              icon={<HistoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="Treatment"
              disabled={!batchId}
            />
            <Tab
              icon={<WithdrawalIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="Withdrawal"
              disabled={!batchId}
            />
            <Tab
              icon={<ComplianceIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="Compliance"
              disabled={!batchId}
            />
          </Tabs>
        </Box>

        {/* ── Details Tab ── */}
        <TabPanel value={tabValue} index={0}>
          <Box px={3}>
            {/* Bird strain cards */}
            {((procurement.birds ?? []).length > 0 || !isDelivered) && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <SectionLabel>Bird Strains ({(procurement.birds ?? []).length})</SectionLabel>
                  {!isDelivered && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setAddingBird(true)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: '#ff9800',
                        borderColor: 'rgba(255,152,0,0.35)',
                        borderRadius: 1.5,
                        mb: 1.5,
                        '&:hover': { borderColor: '#ff9800', bgcolor: 'rgba(255,152,0,0.06)' },
                      }}
                    >
                      + Add Bird Group
                    </Button>
                  )}
                </Box>
                {(procurement.birds ?? []).length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', mb: 2 }}
                  >
                    No bird groups added yet.
                  </Typography>
                )}
                <Box display="flex" gap={2} flexWrap="wrap">
                  {(procurement.birds ?? []).map((bird, i) => {
                    const accent = bird.color?.toLowerCase() === 'white' ? '#42a5f5' : '#a1887f';
                    const survivalPct =
                      bird.quantity_bought > 0
                        ? Math.round((bird.current_quantity / bird.quantity_bought) * 100)
                        : null;
                    return (
                      <Paper
                        key={bird.id}
                        elevation={0}
                        sx={{
                          flex: 1,
                          minWidth: 260,
                          p: 0,
                          border: `1px solid ${accent}22`,
                          borderRadius: 2.5,
                          overflow: 'hidden',
                          background: '#111318',
                        }}
                      >
                        {/* Card header */}
                        <Box
                          sx={{
                            px: 2.5,
                            py: 1.75,
                            background: `linear-gradient(135deg, ${accent}14 0%, ${accent}06 100%)`,
                            borderBottom: `1px solid ${accent}18`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: 1,
                              bgcolor: `${accent}20`,
                              border: `1px solid ${accent}40`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '0.62rem', fontWeight: 800, color: accent }}
                            >
                              {i + 1}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
                            {bird.color.charAt(0).toUpperCase() + bird.color.slice(1)} ·{' '}
                            {bird.birds_type}
                          </Typography>
                          {bird.date_delivered && (
                            <Chip
                              label={formatDate(bird.date_delivered)}
                              size="small"
                              sx={{
                                ml: 'auto',
                                height: 18,
                                fontSize: '0.58rem',
                                fontWeight: 600,
                                bgcolor: 'rgba(76,175,80,0.12)',
                                color: '#4caf50',
                                border: '1px solid rgba(76,175,80,0.25)',
                              }}
                            />
                          )}
                          {!isDelivered && (
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(bird as Bird)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: 'rgba(25,118,210,0.12)',
                                  '&:hover': { bgcolor: 'rgba(25,118,210,0.22)' },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 13, color: '#42a5f5' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setDeletingBird(bird as Bird)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: 'rgba(244,67,54,0.12)',
                                  '&:hover': { bgcolor: 'rgba(244,67,54,0.22)' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 13, color: '#f44336' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {/* Card body */}
                        <Box px={2.5} py={2}>
                          <InfoRow label="Ordered" value={bird.quantity_bought?.toLocaleString()} />
                          <InfoRow
                            label="Current"
                            value={
                              <Typography
                                component="span"
                                fontWeight={700}
                                sx={{ color: '#4caf50' }}
                              >
                                {bird.current_quantity?.toLocaleString()}
                              </Typography>
                            }
                          />
                          <InfoRow label="Unit Price" value={bird.unit_price ?? '—'} />
                          <InfoRow
                            label="Total Cost"
                            value={
                              <Typography
                                component="span"
                                fontWeight={700}
                                sx={{ color: '#42a5f5', fontFamily: 'monospace' }}
                              >
                                {bird.total_cost ?? '—'}
                              </Typography>
                            }
                          />
                          {bird.supplier?.name && (
                            <InfoRow label="Supplier" value={bird.supplier.name} />
                          )}
                        </Box>
                        {/* Survival bar */}
                        {survivalPct !== null && (
                          <Box px={2.5} pb={2}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255,255,255,0.3)',
                                  fontSize: '0.6rem',
                                  letterSpacing: '0.4px',
                                  textTransform: 'uppercase',
                                }}
                              >
                                Survival rate
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{
                                  color:
                                    survivalPct >= 90
                                      ? '#4caf50'
                                      : survivalPct >= 75
                                        ? '#ffa726'
                                        : '#f44336',
                                  fontSize: '0.68rem',
                                }}
                              >
                                {survivalPct}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={survivalPct}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,255,255,0.07)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor:
                                    survivalPct >= 90
                                      ? '#4caf50'
                                      : survivalPct >= 75
                                        ? '#ffa726'
                                        : '#f44336',
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Timeline + Suppliers row */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              {/* Timeline */}
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minWidth: 240,
                  p: 2.5,
                  background: '#111318',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 2.5,
                }}
              >
                <SectionLabel>Timeline</SectionLabel>
                <Box display="flex" flexDirection="column" gap={0}>
                  {[
                    {
                      label: 'Date Procured',
                      value: formatDate(procurement.date_procured),
                      color: '#ff9800',
                      icon: <CalendarIcon sx={{ fontSize: 14 }} />,
                    },
                    {
                      label: 'Date Delivered',
                      value: isDelivered ? formatDate(procurement.date_delivered) : 'Pending',
                      color: isDelivered ? '#4caf50' : '#9e9e9e',
                      icon: <LocalShippingIcon sx={{ fontSize: 14 }} />,
                    },
                    ...(batch
                      ? [
                          {
                            label: 'Batch Status',
                            value: batch.status ?? 'active',
                            color: batch.status === 'ended' ? '#9e9e9e' : '#4caf50',
                            icon: <InventoryIcon sx={{ fontSize: 14 }} />,
                          },
                        ]
                      : []),
                  ].map((item, idx, arr) => (
                    <Box key={item.label} display="flex" gap={2} alignItems="flex-start">
                      {/* Timeline indicator */}
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        sx={{ mt: 0.5, flexShrink: 0 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: `${item.color}15`,
                            border: `1px solid ${item.color}35`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: item.color,
                          }}
                        >
                          {item.icon}
                        </Box>
                        {idx < arr.length - 1 && (
                          <Box
                            sx={{
                              width: 1,
                              flex: 1,
                              bgcolor: 'rgba(255,255,255,0.06)',
                              minHeight: 20,
                              my: 0.5,
                            }}
                          />
                        )}
                      </Box>
                      <Box pb={idx < arr.length - 1 ? 1.5 : 0}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.3)',
                            fontSize: '0.62rem',
                            letterSpacing: '0.4px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: item.color, mt: 0.25 }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Suppliers */}
              {(procurement.birds ?? []).some((b) => b.supplier) &&
                (() => {
                  const seen = new Set<number>();
                  const uniqueSuppliers = (procurement.birds ?? [])
                    .map((b) => b.supplier)
                    .filter(
                      (s): s is NonNullable<typeof s> => !!s && !seen.has(s.id) && !!seen.add(s.id)
                    );
                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1.4,
                        minWidth: 260,
                        p: 2.5,
                        background: '#111318',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2.5,
                      }}
                    >
                      <SectionLabel>Suppliers ({uniqueSuppliers.length})</SectionLabel>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {uniqueSuppliers.map((supplier) => (
                          <Box
                            key={supplier.id}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              background: 'rgba(255,152,0,0.04)',
                              border: '1px solid rgba(255,152,0,0.1)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ color: '#ff9800', mb: 0.75 }}
                            >
                              {supplier.name}
                            </Typography>
                            {supplier.contact_number && (
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <PhoneIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }} />
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.55)' }}
                                >
                                  {supplier.contact_number}
                                </Typography>
                              </Box>
                            )}
                            {supplier.email && (
                              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <EmailIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }} />
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.55)' }}
                                >
                                  {supplier.email}
                                </Typography>
                              </Box>
                            )}
                            {supplier.address && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LocationIcon
                                  sx={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.55)' }}
                                >
                                  {supplier.address}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  );
                })()}
            </Box>

            {/* Bird group mini-cards */}
            {procurement.birds && procurement.birds.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  background: '#111318',
                  border: '1px solid rgba(255,152,0,0.1)',
                  borderRadius: 2.5,
                }}
              >
                <SectionLabel>Live Groups ({procurement.birds.length})</SectionLabel>
                <Box display="flex" flexWrap="wrap" gap={1.5}>
                  {procurement.birds.map((bird: any) => {
                    const mortality = bird.quantity_bought - bird.current_quantity;
                    const survivalPct = bird.quantity_bought
                      ? Math.round((bird.current_quantity / bird.quantity_bought) * 100)
                      : null;
                    const accent = bird.color?.toLowerCase() === 'white' ? '#42a5f5' : '#a1887f';
                    return (
                      <Paper
                        key={bird.id}
                        elevation={0}
                        onClick={() => navigate(`/birds/${bird.id}`)}
                        sx={{
                          px: 2,
                          py: 1.75,
                          minWidth: 175,
                          border: `1px solid ${accent}18`,
                          borderLeft: `3px solid ${accent}`,
                          borderRadius: 2,
                          cursor: 'pointer',
                          background: `${accent}04`,
                          transition: 'transform 0.15s, box-shadow 0.15s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 20px rgba(0,0,0,0.4), 0 0 0 1px ${accent}30`,
                            background: `${accent}08`,
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: accent, fontSize: '0.72rem', letterSpacing: '0.3px' }}
                        >
                          {bird.name ?? `Bird #${bird.id}`}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" mt={0.75}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                          >
                            Current
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#4caf50', fontSize: '0.72rem' }}
                          >
                            {bird.current_quantity?.toLocaleString() ?? '—'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                          >
                            Ordered
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.68rem' }}
                          >
                            {bird.quantity_bought?.toLocaleString() ?? '—'}
                          </Typography>
                        </Box>
                        {mortality > 0 && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                            >
                              Mortality
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: '#f44336', fontSize: '0.68rem' }}
                            >
                              -{mortality.toLocaleString()}{' '}
                              {survivalPct !== null ? `(${survivalPct}%)` : ''}
                            </Typography>
                          </Box>
                        )}
                        {survivalPct !== null && (
                          <LinearProgress
                            variant="determinate"
                            value={survivalPct}
                            sx={{
                              mt: 1,
                              height: 2.5,
                              borderRadius: 1,
                              bgcolor: 'rgba(255,255,255,0.08)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor:
                                  survivalPct >= 90
                                    ? '#4caf50'
                                    : survivalPct >= 75
                                      ? '#ffa726'
                                      : '#f44336',
                                borderRadius: 1,
                              },
                            }}
                          />
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
            )}
          </Box>
        </TabPanel>

        {/* Schedule Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box px={3}>
            {batchId ? (
              <BatchScheduleContent batchId={batchId} />
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No batch found for this procurement.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Treatment History Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box px={3}>
            {batchId ? (
              <TreatmentHistoryContent batchId={batchId} />
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No batch found for this procurement.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Withdrawal Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box px={3}>
            {batchId ? (
              <WithdrawalStatusContent batchId={batchId} />
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No batch found for this procurement.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box px={3}>
            {batchId ? (
              <ComplianceContent batchId={batchId} />
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No batch found for this procurement.
              </Alert>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* ── Mark as Delivered Dialog ── */}
      <Dialog
        open={deliveringProcurement}
        onClose={() => setDeliveringProcurement(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(76,175,80,0.25)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Mark as Delivered</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 2.5 }}>
            Enter the actual number of birds received for each group.
          </Typography>

          {/* Shared delivery date */}
          <TextField
            label="Delivery Date"
            size="small"
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            fullWidth
            sx={{ mb: 3 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {/* Per-bird-group quantity rows */}
          <Box display="flex" flexDirection="column" gap={2}>
            {(procurement?.birds ?? []).map((b: any) => {
              const accent = b.color?.toLowerCase() === 'white' ? '#42a5f5' : '#a1887f';
              return (
                <Box
                  key={b.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${accent}22`,
                    background: `${accent}06`,
                  }}
                >
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
                      {b.identifier}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                      Ordered: {b.quantity_bought?.toLocaleString()}
                    </Typography>
                  </Box>
                  <TextField
                    label="Delivered"
                    size="small"
                    type="number"
                    value={deliveryQtys[b.id] ?? ''}
                    onChange={(e) =>
                      setDeliveryQtys((prev) => ({ ...prev, [b.id]: e.target.value }))
                    }
                    sx={{ width: 130 }}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeliveringProcurement(false)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={markDeliveredMutation.isPending || !deliveryDate}
            onClick={handleConfirmDelivery}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
            }}
          >
            {markDeliveredMutation.isPending ? 'Saving…' : 'Confirm Delivery'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Bird Group Dialog ── */}
      <Dialog
        open={addingBird}
        onClose={() => setAddingBird(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Add Bird Group</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Identifier"
              size="small"
              value={addBirdForm.identifier}
              onChange={(e) => setAddBirdForm({ ...addBirdForm, identifier: e.target.value })}
              fullWidth
            />
            <TextField
              label="Color"
              size="small"
              select
              value={addBirdForm.color}
              onChange={(e) => setAddBirdForm({ ...addBirdForm, color: e.target.value })}
              fullWidth
            >
              <MenuItem value="White">White</MenuItem>
              <MenuItem value="Brown">Brown</MenuItem>
            </TextField>
            <TextField
              label="Quantity"
              size="small"
              type="number"
              value={addBirdForm.quantity_bought}
              onChange={(e) => setAddBirdForm({ ...addBirdForm, quantity_bought: e.target.value })}
              fullWidth
              slotProps={{ htmlInput: { min: 1 } }}
            />
            <TextField
              label="Unit Price (GH₵)"
              size="small"
              type="number"
              value={addBirdForm.unit_price}
              onChange={(e) => setAddBirdForm({ ...addBirdForm, unit_price: e.target.value })}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
              helperText="Price per bird"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddingBird(false)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              createBirdMutation.isPending ||
              !addBirdForm.identifier ||
              !addBirdForm.quantity_bought ||
              parseInt(addBirdForm.quantity_bought, 10) < 1
            }
            onClick={handleAddBird}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {createBirdMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Procurement Quantity Dialog ── */}
      <Dialog
        open={editingProcQty}
        onClose={() => setEditingProcQty(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>
          Update Quantity Procured
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Total Quantity"
            size="small"
            type="number"
            value={procQtyForm}
            onChange={(e) => setProcQtyForm(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            slotProps={{ htmlInput: { min: 1 } }}
            helperText="Total number of birds in this procurement order"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditingProcQty(false)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              updateProcurementMutation.isPending || !procQtyForm || parseInt(procQtyForm, 10) < 1
            }
            onClick={handleSaveProcQty}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {updateProcurementMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Bird Dialog ── */}
      <Dialog
        open={!!editingBird}
        onClose={() => setEditingBird(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(255,152,0,0.2)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Edit Bird Group</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Identifier"
              size="small"
              value={editForm.identifier}
              onChange={(e) => setEditForm({ ...editForm, identifier: e.target.value })}
              fullWidth
            />
            <TextField
              label="Color"
              size="small"
              select
              value={editForm.color}
              onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
              fullWidth
            >
              <MenuItem value="White">White</MenuItem>
              <MenuItem value="Brown">Brown</MenuItem>
            </TextField>
            <TextField
              label="Quantity Delivered"
              size="small"
              type="number"
              value={editForm.quantity_bought}
              onChange={(e) => setEditForm({ ...editForm, quantity_bought: e.target.value })}
              fullWidth
              slotProps={{ htmlInput: { min: 1 } }}
              helperText="Actual number of birds received"
            />
            <TextField
              label="Unit Price (GH₵)"
              size="small"
              type="number"
              value={editForm.unit_price}
              onChange={(e) => setEditForm({ ...editForm, unit_price: e.target.value })}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
              helperText="Price per bird"
            />
            <TextField
              label="Laying Start Date"
              size="small"
              type="date"
              value={editForm.laying_start_date}
              onChange={(e) => setEditForm({ ...editForm, laying_start_date: e.target.value })}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Leave blank to leave unchanged"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditingBird(null)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updateBirdMutation.isPending || !editForm.identifier}
            onClick={handleSaveEdit}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            }}
          >
            {updateBirdMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Bird Confirmation ── */}
      <Dialog
        open={!!deletingBird}
        onClose={() => setDeletingBird(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              border: '1px solid rgba(244,67,54,0.25)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 700 }}>Delete Bird Group?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            This will permanently delete{' '}
            <strong style={{ color: '#fff' }}>{deletingBird?.identifier}</strong> (
            {deletingBird?.quantity_bought?.toLocaleString()} birds) from this procurement. This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeletingBird(null)}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={deleteBirdMutation.isPending}
            onClick={handleConfirmDelete}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
            }}
          >
            {deleteBirdMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcurementDetailPage;
